import { motion } from "framer-motion";
import { Zap, Clock, DollarSign, ShieldCheck, PhoneCall, ArrowRight } from "lucide-react";

const perks = [
  {
    icon: Clock,
    title: "Funded in 4 hours",
    desc: "Skip the bank. Skip the line. Real underwriters on standby right now.",
  },
  {
    icon: DollarSign,
    title: "$5K – $250K",
    desc: "Same-day emergency cash advance tied to your daily card sales.",
  },
  {
    icon: ShieldCheck,
    title: "No collateral",
    desc: "Revenue-based approval. Bad credit? OK. Prior denials? Doesn't matter.",
  },
  {
    icon: PhoneCall,
    title: "Real human, right now",
    desc: "Call and speak with a funding specialist within 60 seconds.",
  },
];

const useCases = [
  "Cover a surprise payroll",
  "Urgent inventory or POs",
  "Emergency equipment repair",
  "Tax bill or vendor demand",
  "Bridge a slow week",
  "Take advantage of a time-boxed deal",
];

export default function LightningFunding({ onApply }) {
  return (
    <section
      id="lightning-funding"
      data-testid="lightning-section"
      className="relative py-24 md:py-32 overflow-hidden bg-[#0b0510] text-white"
    >
      {/* dramatic background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-orange-500/25 blur-[120px]" />
        <div className="absolute top-40 -right-40 w-[600px] h-[600px] rounded-full bg-fuchsia-600/30 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-amber-400/15 blur-[100px]" />
      </div>

      {/* subtle grid lines */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400/20 to-fuchsia-500/20 border border-amber-400/40 backdrop-blur px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300"
          >
            <Zap className="w-3.5 h-3.5 fill-amber-300" /> Emergency Funding · MCA-fast
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-black mt-6 tracking-tighter leading-[0.95]"
          >
            Lightning{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-fuchsia-400 bg-clip-text text-transparent">
                Funding
              </span>
              <Zap className="absolute -top-4 -right-8 md:-right-12 w-8 h-8 md:w-12 md:h-12 text-amber-300 fill-amber-300 animate-float" />
            </span>
            <br className="hidden md:block" />
            <span className="text-white/90">when you need it now.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed"
          >
            For operators who need cash <span className="text-amber-300 font-semibold">today</span> —
            not next week. Same-day approval. Funded in as fast as{" "}
            <span className="text-amber-300 font-semibold">4 hours</span>. Revenue-based, no
            collateral, no bank runaround.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-wrap justify-center items-center gap-4"
          >
            <button
              data-testid="lightning-apply-cta"
              onClick={() =>
                onApply?.({
                  funding_purpose: "Lightning Funding (Emergency)",
                  funding_amount: 50000,
                })
              }
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-fuchsia-600 hover:brightness-110 text-white font-bold px-8 py-4 text-sm tracking-[0.15em] uppercase shadow-2xl shadow-orange-500/40 hover:-translate-y-0.5 transition-all"
            >
              Get Emergency Funding
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </button>
            <a
              href="tel:+13322301915"
              data-testid="lightning-call"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/20 hover:bg-white/10 backdrop-blur text-white font-bold px-7 py-4 text-sm tracking-[0.15em] uppercase transition-all"
            >
              <PhoneCall className="w-4 h-4" />
              Call now · (332) 230-1915
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-4 text-[11px] text-white/40 uppercase tracking-widest font-semibold"
          >
            Applied by 2 PM local? Funded same business day.
          </motion.p>
        </div>

        {/* Perks grid */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {perks.map((p, idx) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.06 }}
              data-testid={`lightning-perk-${idx}`}
              className="group rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 p-6 hover:bg-white/10 hover:-translate-y-1 hover:border-amber-300/40 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center shadow-lg shadow-orange-500/40 mb-4">
                <p.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display text-lg font-bold text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Use cases + testimonial strip */}
        <div className="mt-14 grid lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 p-8"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300 mb-4">
              Common lightning-funding situations
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {useCases.map((u) => (
                <div key={u} className="flex items-center gap-3 text-white/80">
                  <Zap className="w-3.5 h-3.5 text-amber-300 fill-amber-300 flex-shrink-0" />
                  <span className="text-sm font-medium">{u}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-3xl bg-gradient-to-br from-amber-400/15 via-orange-500/15 to-fuchsia-600/15 border border-amber-400/30 p-8 relative overflow-hidden"
          >
            <Zap className="absolute -top-2 -right-2 w-24 h-24 text-amber-300/10 fill-amber-300/10" />
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300 mb-3">
              Real story
            </p>
            <p className="text-white/90 text-base leading-relaxed">
              &ldquo;POS died on a Saturday. Rocket Cap wired{" "}
              <span className="font-bold text-white">$32K by 3 PM</span>. New terminals live
              Monday morning.&rdquo;
            </p>
            <p className="mt-4 text-xs text-white/60">
              — Marco T., cafe owner · <span className="text-amber-300">funded in 3h 47m</span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
